import { t } from '@lingui/core/macro';
import { Alert, Anchor, Box, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

import { brightenOvLighting, configureOvExternalLibs } from './ovExternalLibs';

/**
 * Classify the raw text the online-3d-viewer overlay surfaced on a load
 * failure.
 *
 * The library has no error callback — it writes a status string into a
 * transient ``<div>`` inside the container — so we read that text back and
 * decide which troubleshooting hint to show.
 *
 * - ``no-meshes`` — occt-import-js successfully parsed the STEP but it has no
 *   tessellable solid bodies (typical SolidWorks "Surfaces" export, sheet-metal
 *   open shells, or pure-NURBS AP242 without tessellation hints). The CDN
 *   hint would be misleading here — it's a CAD-export problem.
 * - ``load-error`` — anything else (network fetch failed, ZIP archive
 *   malformed, unsupported format). The /static/3d-libs/ pointer is the right
 *   troubleshooting target.
 */
type ViewerErrorKind = 'no-meshes' | 'load-error';

function classifyViewerError(text: string): ViewerErrorKind {
  return /no.*mesh|doesn.?t contain any meshes/i.test(text)
    ? 'no-meshes'
    : 'load-error';
}

// File extensions that can be rendered in-browser by online-3d-viewer.
export const VIEWABLE_EXTENSIONS = new Set([
  'step',
  'stp',
  'stl',
  'obj',
  'gltf',
  'glb',
  'ply',
  '3mf'
]);

export function getExtension(url?: string | null): string | null {
  if (!url) return null;
  const clean = url.split('?')[0].split('#')[0];
  const dot = clean.lastIndexOf('.');
  if (dot < 0) return null;
  return clean.slice(dot + 1).toLowerCase();
}

/**
 * Make an uploaded-file URL same-origin.
 *
 * Behind the reverse proxy the API can hand out absolute ``http://`` URLs for
 * files (the proxy terminates TLS), which the browser blocks as mixed content
 * on an https page. Uploaded files are always served from ``/media/`` on the
 * same host, so use the path only.
 */
export function toSameOriginUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.startsWith('/media/')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // keep the URL as it is
  }
  return url;
}

/**
 * Pick the best browser-renderable URL for a part: prefer the dedicated
 * preview file, fall back to the source if it is itself a viewable format.
 * Returns null when nothing can be rendered in-browser.
 */
export type Model3DVariant = 'preview' | 'source';

type Model3DFiles = {
  model_3d?: string | null;
  model_3d_preview?: string | null;
};

function viewableUrl(url?: string | null): string | null {
  return url && VIEWABLE_EXTENSIONS.has(getExtension(url) ?? '')
    ? toSameOriginUrl(url)
    : null;
}

/**
 * Variants (preview file / source file) that the browser can render, e.g. a
 * light GLB preview next to the original STEP. Empty when nothing is viewable.
 */
export function getModel3DVariants(part: Model3DFiles): Model3DVariant[] {
  const variants: Model3DVariant[] = [];
  if (viewableUrl(part?.model_3d_preview)) variants.push('preview');
  if (viewableUrl(part?.model_3d)) variants.push('source');
  return variants;
}

export function getModel3DRenderUrl(
  part: Model3DFiles,
  variant?: Model3DVariant
): string | null {
  const previewUrl = viewableUrl(part?.model_3d_preview);
  const sourceUrl = viewableUrl(part?.model_3d);
  if (variant === 'source') return sourceUrl ?? previewUrl;
  return previewUrl ?? sourceUrl;
}

interface Model3DViewerProps {
  /** Browser-renderable model URL (STEP/STL/OBJ/GLTF/GLB/PLY/3MF). */
  renderUrl: string | null;
  /** Canvas height; defaults to 560px. */
  height?: number | string;
}

/**
 * Interactive 3D viewer for a single renderable model URL.
 *
 * Wraps online-3d-viewer (dynamically imported to keep three.js out of the
 * main bundle) and surfaces the library's transient load errors as React
 * state, since the lib only writes them into a throwaway <div>.
 */
export function Model3DViewer({ renderUrl, height = 560 }: Model3DViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<any>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const errorKind: ViewerErrorKind | null = viewerError
    ? classifyViewerError(viewerError)
    : null;

  useEffect(() => {
    let cancelled = false;
    let mutationObserver: MutationObserver | null = null;
    setViewerError(null);

    // Tear down any existing viewer instance.
    if (viewerRef.current) {
      try {
        viewerRef.current.Destroy?.();
      } catch {
        // best-effort cleanup
      }
      viewerRef.current = null;
    }
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    if (!renderUrl || !containerRef.current) {
      return () => {
        cancelled = true;
      };
    }

    // Dynamic import keeps the heavy three.js bundle out of the main chunk.
    import('online-3d-viewer')
      .then((OV) => {
        if (cancelled || !containerRef.current) return;
        // Point the lib's STEP / 3DM / Draco loaders at our locally-vendored
        // copy so they don't depend on cdn.jsdelivr.net being reachable from
        // the shop-floor network. Idempotent — safe to call on every mount.
        configureOvExternalLibs(OV);
        try {
          const viewer = new OV.EmbeddedViewer(containerRef.current, {
            backgroundColor: new OV.RGBAColor(245, 245, 245, 255),
            defaultColor: new OV.RGBColor(120, 144, 168),
            edgeSettings: new OV.EdgeSettings(false, new OV.RGBColor(0, 0, 0), 1),
            environmentSettings: new OV.EnvironmentSettings([], false),
            // The library resets the lights when the model is set
            onModelLoaded: () => brightenOvLighting(viewer)
          });
          viewer.LoadModelFromUrlList([renderUrl]);
          viewerRef.current = viewer;

          // The lib writes load errors into a transient <div> inside the
          // container instead of exposing a callback. Surface those as React
          // state so the user sees them outside the canvas overlay.
          //
          // The overlay typically writes the wrapper ("Failed to import
          // model.") and the cause ("The model doesn't contain any meshes.")
          // as two DOM nodes. ``innerText`` concatenates both — we strip the
          // generic wrapper if a cause is present and collapse repeated lines
          // so the alert never shows the same sentence twice.
          if (containerRef.current) {
            mutationObserver = new MutationObserver(() => {
              const raw = containerRef.current?.innerText?.trim() ?? '';
              if (!/failed|unknown error|mesh/i.test(raw)) return;
              // Split on newlines, drop empties, de-dup while preserving order.
              const seen = new Set<string>();
              const lines: string[] = [];
              for (const line of raw.split(/\r?\n+/)) {
                const t = line.trim();
                if (!t || seen.has(t)) continue;
                seen.add(t);
                lines.push(t);
              }
              // Drop the generic prefix sentence when a more specific cause
              // follows — keeps the visible message a single concrete reason.
              const specific = lines.find((l) =>
                /mesh|cannot|invalid|not.{0,4}supported|format/i.test(l)
              );
              const next = specific ?? lines.join(' ');
              setViewerError((prev) => (prev === next ? prev : next));
            });
            mutationObserver.observe(containerRef.current, {
              childList: true,
              subtree: true,
              characterData: true
            });
          }
        } catch (err: any) {
          setViewerError(err?.message ?? 'Failed to initialize 3D viewer');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setViewerError(
          err?.message ??
            'online-3d-viewer is not installed — run `yarn install` after pulling.'
        );
      });

    return () => {
      cancelled = true;
      mutationObserver?.disconnect();
      if (viewerRef.current) {
        try {
          viewerRef.current.Destroy?.();
        } catch {
          // best-effort
        }
        viewerRef.current = null;
      }
    };
  }, [renderUrl]);

  return (
    <>
      <Box
        ref={containerRef}
        style={{
          width: '100%',
          height: typeof height === 'number' ? `${height}px` : height,
          minHeight: '240px',
          position: 'relative',
          borderRadius: 4,
          overflow: 'hidden'
        }}
      />
      {viewerError && (
        <Alert color='red' icon={<IconAlertCircle />} mt='xs'>
          <Stack gap={4}>
            <Text size='sm'>
              {errorKind === 'no-meshes'
                ? t`The 3D model could not be loaded: the file contains no meshes the in-browser viewer can render.`
                : t`The 3D model could not be loaded.`}
            </Text>
            <Text size='xs' c='dimmed'>
              {viewerError}
            </Text>
            {errorKind === 'no-meshes' &&
              (getExtension(renderUrl) === 'step' ||
                getExtension(renderUrl) === 'stp') && (
                <Text size='xs' c='dimmed'>
                  {t`The STEP file was read but contains no solid bodies the in-browser viewer can tessellate — typical of a SolidWorks export that wrote surfaces instead of solids, or of sheet-metal / open-shell models. Re-export from the CAD app as STEP AP242 with "Solid/Surface geometry" selected, or attach an STL/GLB preview file via the field below.`}
                </Text>
              )}
            {errorKind === 'load-error' &&
              (getExtension(renderUrl) === 'step' ||
                getExtension(renderUrl) === 'stp') && (
                <Text size='xs' c='dimmed'>
                  {t`STEP parsing needs occt-import-js to be reachable at /static/3d-libs/. If the file is missing, vendor it into src/backend/InvenTree/web/static/3d-libs/ (see ovExternalLibs.ts).`}
                </Text>
              )}
            {renderUrl && (
              <Text size='xs' c='dimmed'>
                <Anchor href={renderUrl} target='_blank' rel='noopener'>
                  {t`Download the file to inspect it locally`}
                </Anchor>
              </Text>
            )}
          </Stack>
        </Alert>
      )}
    </>
  );
}
